import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import SiteTemplateRenderer from '../components/new-components/PageBuilder/SiteTemplateRenderer';
import type { BlockInstance } from '../components/new-components/PageBuilder/types';

interface PageContent {
  blocks?: BlockInstance[];
  settings?: { siteTemplate?: string; sections?: Record<string, unknown>; [key: string]: unknown };
}

interface SitePageData {
  id: string;
  title: string;
  slug: string;
  // Public endpoint returns `content`; admin instances use `section_content`
  content?: PageContent;
  section_content?: PageContent;
  publishedAt: string;
  updatedAt: string;
}

const SitePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<SitePageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);

    api.get(`/site-pages/${encodeURIComponent(slug)}`)
      .then((res: unknown) => {
        const body = (res as Record<string, unknown>)?.data ?? res;
        const data = (body as Record<string, unknown>)?.data ?? body;
        // Accept either `content` (legacy) or `section_content` (block-based)
        if (data && typeof data === 'object') {
          const d = data as SitePageData;
          if (d.content || d.section_content) {
            setPage(d);
          } else {
            setNotFound(true);
          }
        } else {
          setNotFound(true);
        }
      })
      .catch(() => {
        setNotFound(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (notFound || !page) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background text-center px-6">
        <h1 className="text-4xl font-bold mb-2">404</h1>
        <p className="text-muted-foreground mb-6">This page doesn't exist or hasn't been published yet.</p>
        <a href="/" className="text-primary hover:underline text-sm">Go home</a>
      </div>
    );
  }

  // Resolve content source — prefer section_content (block-based), fall back to content (template-based)
  const sc = page.section_content ?? page.content;
  const siteTemplate = sc?.settings?.siteTemplate;
  const blocks = sc?.blocks ?? [];

  // Always render SiteTemplateRenderer for the chrome (nav + footer).
  // Blocks render inside the template's content zone — same composition model as Squarespace.
  return (
    <div className="min-h-screen bg-background">
      <SiteTemplateRenderer
        templateId={siteTemplate || 'generic'}
        sections={(sc?.settings?.sections ?? {}) as Record<string, Record<string, unknown>>}
        blocks={blocks}
        blockSettings={sc?.settings ?? {}}
      />
    </div>
  );
};

export default SitePage;
